/**
 * /payroll-l3/compliance-alerts — Union compliance alerts list.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import { api } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll-l3/compliance-alerts")({
  component: ComplianceAlertsPage,
});

interface ComplianceAlert {
  id: string;
  alert_type: string;
  scope: string | null;
  scope_id: string | null;
  severity: "info" | "warning" | "error" | string;
  title: string | null;
  detail: Record<string, unknown> | null;
  ai_explanation: string | null;
  suggested_action: string | null;
  due_date: string | null;
  is_resolved: boolean;
  acknowledged_at: string | null;
  created_at: string;
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function severityChip(s: string) {
  const map: Record<string, "default" | "info" | "warning" | "error"> = {
    info: "info",
    warning: "warning",
    error: "error",
    critical: "error",
  };
  return <Chip label={s} size="small" color={map[s] ?? "default"} />;
}

function ComplianceAlertsPage() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState({
    alert_type: "",
    severity: "",
    is_resolved: "false",
  });

  const params = new URLSearchParams();
  if (filters.alert_type) params.set("alert_type", filters.alert_type);
  if (filters.severity) params.set("severity", filters.severity);
  if (filters.is_resolved !== "") params.set("is_resolved", filters.is_resolved);
  const qs = params.toString();

  const alertsQ = useQuery({
    queryKey: ["payroll-l3", "compliance-alerts", filters],
    queryFn: () =>
      api<ComplianceAlert[]>(`/api/payroll-l3/compliance-alerts${qs ? `?${qs}` : ""}`),
  });

  const resolveMut = useMutation({
    mutationFn: (id: string) =>
      api(`/api/payroll-l3/compliance-alerts/${id}/resolve`, { method: "PATCH" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payroll-l3", "compliance-alerts"] }),
  });

  // Run the AI compliance audit on demand (also runs nightly via scheduler)
  const runAuditMut = useMutation({
    mutationFn: () =>
      api<{ total_alerts: number }>("/api/payroll-l3/ai/compliance-audit/run", {
        method: "POST",
        body: JSON.stringify({}),
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["payroll-l3", "compliance-alerts"] }),
  });

  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={600}>Compliance Alerts</Typography>
        <Stack direction="row" gap={1} alignItems="center">
          <Button
            size="small"
            variant="outlined"
            startIcon={<AutoAwesomeIcon />}
            onClick={() => runAuditMut.mutate()}
            disabled={runAuditMut.isPending}
          >
            {runAuditMut.isPending ? "Running…" : "Run AI audit"}
          </Button>
          <HelpHint docPath="payroll-l3/ai-overlay.md" label="AI overlay help" />
        </Stack>
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Alerts raised by the union pay-run hook AND the nightly AI audit
        (CBA expiry, misclassification, missing dues authorization,
        retroactive rate increases, fringe underpayment, remittance
        discrepancies, apprentice-ratio violations).
      </Typography>
      {runAuditMut.data && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => runAuditMut.reset()}>
          AI audit complete — {runAuditMut.data.total_alerts} alert(s) generated or refreshed.
        </Alert>
      )}
      {runAuditMut.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          AI audit failed: {(runAuditMut.error as Error).message}
        </Alert>
      )}

      <Stack direction="row" spacing={2} mb={2}>
        <TextField
          select
          size="small"
          label="Resolved"
          value={filters.is_resolved}
          onChange={(e) => setFilters((p) => ({ ...p, is_resolved: e.target.value }))}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="false">Open</MenuItem>
          <MenuItem value="true">Resolved</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Severity"
          value={filters.severity}
          onChange={(e) => setFilters((p) => ({ ...p, severity: e.target.value }))}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="info">Info</MenuItem>
          <MenuItem value="warning">Warning</MenuItem>
          <MenuItem value="error">Error</MenuItem>
          <MenuItem value="critical">Critical</MenuItem>
        </TextField>
        <TextField
          size="small"
          label="Type"
          value={filters.alert_type}
          onChange={(e) => setFilters((p) => ({ ...p, alert_type: e.target.value }))}
          placeholder="apprentice_ratio_violation"
          sx={{ minWidth: 240 }}
        />
      </Stack>

      {alertsQ.isLoading && <CircularProgress size={24} />}
      {alertsQ.error && <Alert severity="error">Failed to load alerts.</Alert>}

      {alertsQ.data && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Severity</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Resolved</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {alertsQ.data.map((a) => {
              const isOpen = expanded === a.id;
              const hasDetail = !!(a.ai_explanation || a.detail);
              return (
                <>
                  <TableRow
                    key={a.id}
                    hover
                    sx={{
                      opacity: a.is_resolved ? 0.6 : 1,
                      cursor: hasDetail ? "pointer" : "default",
                    }}
                    onClick={() =>
                      hasDetail && setExpanded(isOpen ? null : a.id)
                    }
                  >
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                      {a.alert_type}
                    </TableCell>
                    <TableCell>{severityChip(a.severity)}</TableCell>
                    <TableCell>
                      <Stack>
                        <span>{a.title ?? "—"}</span>
                        {a.suggested_action && (
                          <Typography variant="caption" color="text.secondary">
                            Suggested: {a.suggested_action}
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{fmtDate(a.created_at)}</TableCell>
                    <TableCell>
                      {a.is_resolved ? (
                        <Chip label="Resolved" size="small" color="success" />
                      ) : (
                        <Chip label="Open" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      {!a.is_resolved && (
                        <Button
                          size="small"
                          startIcon={<CheckCircleIcon />}
                          onClick={(e) => {
                            e.stopPropagation();
                            resolveMut.mutate(a.id);
                          }}
                          disabled={resolveMut.isPending}
                        >
                          Mark resolved
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  {isOpen && hasDetail && (
                    <TableRow>
                      <TableCell colSpan={6} sx={{ bgcolor: "action.hover", py: 2 }}>
                        <Stack spacing={1}>
                          {a.ai_explanation && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                AI explanation
                              </Typography>
                              <Typography variant="body2">
                                {a.ai_explanation}
                              </Typography>
                            </Box>
                          )}
                          {a.detail && (
                            <Box>
                              <Typography variant="caption" color="text.secondary">
                                Detail
                              </Typography>
                              <Box
                                component="pre"
                                sx={{
                                  m: 0,
                                  fontSize: 12,
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                {JSON.stringify(a.detail, null, 2)}
                              </Box>
                            </Box>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              );
            })}
            {alertsQ.data.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: "text.secondary", py: 4 }}>
                  No alerts in this view — all clear.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
